/**
 * SQ-283: the three job pricing rules (small-job hourly rate increase,
 * minimum job charge, travel charge) as applied to one record. Mirror of
 * the server's `Shared/Utils/JobPricing.cs` and Android's
 * `core/pricing/JobPricing.kt`, line for line; all three run the vectors in
 * `docs/fixtures/job-pricing-vectors.json`.
 *
 * Order, from the ticket: the increase applies to labor only; equipment and
 * cost items are added; that subtotal is compared to the minimum and the
 * shortfall added; travel goes on last, outside the minimum on both sides.
 * Each added amount is a whole dollar, the increase rounded BEFORE the
 * minimum check. Duration is wall-clock (labor minutes ÷ crew), so crew size
 * never moves the point on the curve.
 */

export const MAX_INCREASE_PERCENT = 300;

export interface JobPricingRules {
  minimumJobCharge: number;
  startingIncreasePercent: number;
  /** Minutes at which the increase reaches zero; 0 = it never comes down. */
  returnMinutes: number;
  freeMiles: number;
  perMileRate: number;
}

export const RULES_OFF: JobPricingRules = {
  minimumJobCharge: 0,
  startingIncreasePercent: 0,
  returnMinutes: 0,
  freeMiles: 0,
  perMileRate: 0,
};

export const rulesAreOff = (rules: JobPricingRules): boolean =>
  rules.minimumJobCharge <= 0 && rules.startingIncreasePercent <= 0 && rules.perMileRate <= 0;

/** What the record adds up to before the rules: the sums the job total area already shows. */
export interface JobPricingInput {
  laborCost: number;
  /** TOTAL labor minutes (crew folded in): the estimated minutes, never tracked time. */
  laborMinutes: number;
  crewCount: number;
  equipmentCost: number;
  costItemsTotal: number;
  /** One-way driving miles from the shop; null when unknown (no travel line). */
  travelMiles: number | null;
}

/** The three "Added for …" lines. A line appears only when its amount is above zero. */
export interface JobPricingResult {
  smallJobRateAmount: number;
  minimumTopUpAmount: number;
  travelAmount: number;
}

export const NO_ADDITIONS: JobPricingResult = { smallJobRateAmount: 0, minimumTopUpAmount: 0, travelAmount: 0 };

export const addedTotal = (result: JobPricingResult): number =>
  result.smallJobRateAmount + result.minimumTopUpAmount + result.travelAmount;

// Half up, like the server's AwayFromZero on positive amounts. Computed in
// cents first so 38.5 from 70 × 55 % does not land at 38.499999.
// The 1e-9 nudge counters binary error: 76.99 × 50 / 100 is 38.494999… as a
// double but 38.495 exactly on the server's decimals, and both must land on $39.
const wholeDollars = (amount: number): number => Math.round(Math.round(amount * 100 + 1e-9) / 100 + 1e-9);

/** The percent the curve gives at `durationMinutes` of wall-clock time. */
export const increasePercentAt = (rules: JobPricingRules, durationMinutes: number): number => {
  const start = Math.min(MAX_INCREASE_PERCENT, Math.max(0, rules.startingIncreasePercent));
  if (start <= 0) return 0;
  if (rules.returnMinutes <= 0) return start;
  if (durationMinutes >= rules.returnMinutes) return 0;
  return start * (1 - durationMinutes / rules.returnMinutes);
};

export const applyJobPricing = (input: JobPricingInput, rules: JobPricingRules | null | undefined): JobPricingResult => {
  if (!rules || rulesAreOff(rules)) return NO_ADDITIONS;

  // No crew: there is no labor price to increase (SQ-522 B18); the minimum
  // and travel still apply to equipment and cost items.
  let increase = 0;
  if (input.crewCount > 0 && input.laborCost > 0) {
    const duration = input.laborMinutes / input.crewCount;
    const pct = increasePercentAt(rules, duration);
    increase = wholeDollars(input.laborCost * pct / 100);
  }

  const subtotal = input.laborCost + increase + input.equipmentCost + input.costItemsTotal;
  const topUp = rules.minimumJobCharge > subtotal ? wholeDollars(rules.minimumJobCharge - subtotal) : 0;

  let travel = 0;
  if (rules.perMileRate > 0 && input.travelMiles !== null && input.travelMiles !== undefined) {
    const billable = Math.max(0, input.travelMiles - rules.freeMiles);
    travel = wholeDollars(billable * rules.perMileRate);
  }

  return { smallJobRateAmount: increase, minimumTopUpAmount: topUp, travelAmount: travel };
};

/** The job total: the three sums plus whatever the rules added. */
export const jobPricingTotal = (input: JobPricingInput, result: JobPricingResult): number =>
  input.laborCost + input.equipmentCost + input.costItemsTotal + addedTotal(result);
